import React, { useEffect, useMemo, useReducer, useState, useRef } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";

import styles from "./CustomRestaurantInfo.module.css";
import * as Api from "../../api";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete1.svg";

import { CategoriesFields, SocialMediaFields, SectionNames, MetaData, FieldTypes } from "./PortalSettings/PortalState/SectionDataMetaData";
import { updateRestaurantData } from "../../redux/actions/userDataActions";
import { getImgDetail, mapStateToServer } from "./PortalSettings/PortalState/dataTransformerUtilities";
import { mapServerToState } from "./PortalSettings/PortalState/dataTransformerUtilities";
import portalReducer from "./PortalSettings/PortalState/PortalSettingsReducer";

import Container from "../../components/Container";
import Loader from "../../components/Loader";
import AlertMsg from "../../components/AlertMsg";
import AppDialog from "../../components/AppDialog";
import CategoryGroupDialog from "../../components/CategoryGroupDialog";
import RestaurantHeader from "../../components/RestaurantHeader";

import ImagesTab from "./PortalSettings/ImagesTab";
import SocialMediaPage from "./PortalSettings/SocialMediaPage";
import TabPanelTemplate, { ARTabPanel } from "./PortalSettings/TabPanelTemplate";
import CategoryGroupPage from "./PortalSettings/CategoryGroupPage";
import PaymentSettingsTab from "./PortalSettings/PaymentSettingsTab";
import MultiLanguageTab from "./PortalSettings/MultiLanguageTab";
import TableOrderingMenuLayoutTab from "./PortalSettings/TableOrderingMenuLayoutTab";
import {
  checkSocialMediaFieldError,
  checkCategoryFieldError,
  COLOR_ERROR_TEXT,
  TabPanel,
  getDefaultErrorObject,
  getDefaultChangeObject,
  isSocialMedia,
  PageTabs,
  getTabIndex,
} from "./PortalSettings/PortalUtils";
import WhiteListDomain from "../../components/WhiteListDomain";

const REST_LIST = ["6509e4d7d1197a7aa90df7df"]; //rokka


// Setting values in case of the restaurant is not in "REST_LIST" list. Legacy settings. 
const setArPageSettingsNotInList = (dispatch) => {
  dispatch({ type: SectionNames.arPageSection, payload: { fieldName: "menuItemFontBg", value: "#2F5831" } });
  dispatch({ type: SectionNames.arPageSection, payload: { fieldName: "menuItemFontColor", value: "#FFFFFF" } });
  dispatch({ type: SectionNames.arPageSection, payload: { fieldName: "modalFontColor", value: "#FFFFFF" } });
  dispatch({ type: SectionNames.arPageSection, payload: { fieldName: "userFormDataBg", value: "#CA7128" } });
  dispatch({ type: SectionNames.arPageSection, payload: { fieldName: "userFormDataLabelColor", value: "#FFFFFF" } });
  dispatch({ type: SectionNames.arPageSection, payload: { fieldName: "userFormDataFontColor", value: "#FFFFFF" } });
  dispatch({ type: SectionNames.arPageSection, payload: { fieldName: "buttonBg", value: "#FFFFFF" } });
  dispatch({ type: SectionNames.arPageSection, payload: { fieldName: "buttonTextBg", value: "#D87025" } });
}

const CustomRestaurantInfo = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restaurantId] = useState(
    location.pathname.split("/").length == 4
      ? location.pathname.split("/")[2]
      : ""
  );

  const [msgAlert, setMsgAlert] = useState({ open: false });
  const [imageLoading, setImageLoading] = useState(false);
  const [imageType, setImageType] = useState("");

  const [restaurantCategories, setRestaurantCategories] = useState([]);
  const [categoryDialogState, setCategoryDialogState] = useState({ open: false });

  const [errors, setErrors] = useState(getDefaultErrorObject());
  // errorIndicator quickly tells if there is any error in the form. If number is greater than zero, the form has error.
  // I'm using bit-manipulation to handle its value. Please manipulate the value of indicator only for the form errors.
  const [errorIndicator, setErrorIndicator] = useState(0);
  // Just like errorIndicator, but to tell if there is any change in the form.
  const [changeIndicator, setChangeIndicator] = useState(0);
  // Maintains the data of which field value has been changed.
  const [changeObject, setChangeObject] = useState(getDefaultChangeObject());
  const [currentTab, setCurrentTab] = useState(0);
  const [appDialogState, setAppDialogState] = useState({ open: false });
  const [state, dispatch] = useReducer(portalReducer, mapServerToState(props.restaurantData));
  const originalServerState = useRef(mapServerToState(props.restaurantData));

  useEffect(() => {
    if (!REST_LIST.includes(restaurantId)) {
      setArPageSettingsNotInList(dispatch);
    }
    getRestaurantData();
    getCategoryList();
  }, []);

  //#region Get Server Data
  const getRestaurantData = async () => {
    setLoading(true);
    const response = await Api.getRestaurantData(restaurantId);
    setLoading(false);
    if (response.success) {
      const restaurantData = response.data;
      props.updateRestaurantData(restaurantData)
      const stateData = mapServerToState(restaurantData);
      originalServerState.current = structuredClone(stateData);
      dispatch({ type: "STATE", payload: stateData });
    } else {
      setMsgAlert({ open: true, message: response.msg, msgType: "error" });
    }
  };

  const getCategoryList = () => {
    const filter = `pageSize=100000&pageNum=1&filter__idRestaurant=${params.rest_id}`;
    Api.getMenuCategoryList(filter, params.rest_id).then((response) => {
      if (response.success) {
        const data = response.data.rows.sort((a, b) => a?.order - b?.order);
        setRestaurantCategories(data);
      } else {
        console.log(response)
      }
    });
  };
  //#endregion

  //#region Social_Media
  const addGroupEntry = (section) => {
    const groupLength = state[section].length;
    if (groupLength > 0) {
      const errArr = checkAllFieldsErrors(section, state[section], groupLength - 1);
      if (errArr.length > 0) {
        setGroupErrors(errArr, section, groupLength - 1);
        setErrorIndicator(errorIndicator | (1 << getTabIndex(section)));
        return;
      }
    }

    const newEntry = isSocialMedia(section) ? {
      name: "",
      action: "",
      icon: "",
      url: "",
      iconHeight: "",
      iconWidth: "",
    } : { name: "", categories: [], img: "", imageWidth: 120, imageHeight: 120 }

    const newGroupList = [...state[section], newEntry];
    const tabIndex = getTabIndex(section);

    const newEntryError = checkAllFieldsErrors(section, newGroupList, groupLength);

    setGroupErrors(newEntryError, section, groupLength);
    setErrorIndicator(errorIndicator | (1 << tabIndex));

    setChangeIndicator(changeIndicator | (1 << tabIndex));

    const newChangeObject = new Map(changeObject[section]);
    if (isSocialMedia(section)) {
      newChangeObject.set(`${groupLength}_${SocialMediaFields.name}`, "");
      newChangeObject.set(`${groupLength}_${SocialMediaFields.action}`, "");
      newChangeObject.set(`${groupLength}_${SocialMediaFields.icon}`, "");
      newChangeObject.set(`${groupLength}_${SocialMediaFields.url}`, "");
      newChangeObject.set(`${groupLength}_${SocialMediaFields.iconWidth}`, "");
      newChangeObject.set(`${groupLength}_${SocialMediaFields.iconHeight}`, "");
    } else {
      newChangeObject.set(`${groupLength}_${CategoriesFields.name}`, "");
      newChangeObject.set(`${groupLength}_${CategoriesFields.categories}`, "");
      newChangeObject.set(`${groupLength}_${CategoriesFields.imageWidth}`, "");
      newChangeObject.set(`${groupLength}_${CategoriesFields.imageHeight}`, "");
      newChangeObject.set(`${groupLength}_${CategoriesFields.img}`, "");
    }

    setChangeObject(prev => ({
      ...prev,
      [SectionNames.categoryGroupsTab]: newChangeObject
    }));

    dispatch({ type: section, payload: newGroupList });
  }

  const onRemoveCategory = (categoryGroup, category, index) => {
    const newCategoryGroupList = [...state[SectionNames.categoryGroupsTab]];
    newCategoryGroupList[index].categories = categoryGroup.categories.filter((x) => x._id !== category._id);
    setGroupsChanges(SectionNames.categoryGroupsTab, CategoriesFields.categories, index, newCategoryGroupList[index][CategoriesFields.categories]);
    const errArray = checkGroupErrors(SectionNames.categoryGroupsTab, newCategoryGroupList, index, CategoriesFields.categories);
    setGroupErrors(errArray, SectionNames.categoryGroupsTab, index, CategoriesFields.categories);
    dispatch({ type: SectionNames.categoryGroupsTab, payload: newCategoryGroupList });
  }

  const handleToggleCategory = (category) => {
    const newCategoryGroupList = [...state[SectionNames.categoryGroupsTab]];
    const currentGroup = newCategoryGroupList[categoryDialogState.index];
    // Find if category exists
    const existingIndex = currentGroup.categories.findIndex(
      existingCategory => existingCategory._id === category._id
    );

    // Remove if exists
    if (existingIndex >= 0) currentGroup.categories.splice(existingIndex, 1);
    // Add if doesn't exist
    else currentGroup.categories.push(structuredClone(category));

    const errArray = checkGroupErrors(SectionNames.categoryGroupsTab, newCategoryGroupList, categoryDialogState.index, CategoriesFields.categories);
    setGroupErrors(errArray, SectionNames.categoryGroupsTab, categoryDialogState.index, CategoriesFields.categories);
    setGroupsChanges(SectionNames.categoryGroupsTab, CategoriesFields.categories, categoryDialogState.index, categoryDialogState.data);
    dispatch({ type: SectionNames.categoryGroupsTab, payload: newCategoryGroupList });
  }

  const onCategoriesListEdit = (data, index) => {
    setCategoryDialogState({ open: true, data, index });
  }

  const closeCategoryDialog = () => {
    setCategoryDialogState({ open: false });
  }

  // This list returns a Map that holds all the categories in all the groups. 
  const getCategoriesInGroupsList = useMemo(() => {
    const list = new Map();

    state[SectionNames.categoryGroupsTab].map((group) => {
      group.categories.forEach(category => list.set(category._id, category));
    });

    return list;
  }, [state[SectionNames.categoryGroupsTab]]);
  //#endregion

  //#region Common Social_Media_And_Category_Group
  const handleGroupChange = (section, field, value, index) => {
    const newGroupList = [...state[section]];
    newGroupList[index][field] = value;
    const errArr = checkGroupErrors(section, newGroupList, index, field);
    setGroupErrors(errArr, section, index, field);
    setGroupsChanges(section, field, index, value);
    dispatch({ type: section, payload: newGroupList });
  };

  // Checks for the errors in all the fields of a group(Social Media or Category Group) and returns an array of errors.
  // Error array is an array of tuples with the first element being the key for the error and the second element being the error message.
  const checkAllFieldsErrors = (section, sectionState, index) => {
    const groupFields = isSocialMedia(section) ? SocialMediaFields : CategoriesFields;
    const errorArray = [];
    Object.values(groupFields).forEach(field => {
      const error = isSocialMedia(section) ? checkSocialMediaFieldError(field, sectionState[index][field]) : checkCategoryFieldError(field, sectionState[index][field]);;
      if (error) errorArray.push([`${index}_${field}`, error]);
    });
    return errorArray;
  }
  // Function to check for errors in Social Media/Category Groups fields
  // If fieldname is provided, it will check for a single field(use case: editing a single field) otherwise for the entire entry(use case: adding/deleting an object)
  const checkGroupErrors = (section, sectionState, index, fieldName) => {
    const isSocialMediaTab = isSocialMedia(section);
    // Just check for a single field if fieldName is provided
    if (fieldName) {
      const errorArray = [];
      const error = isSocialMediaTab ? checkSocialMediaFieldError(fieldName, sectionState[index][fieldName]) :
        checkCategoryFieldError(fieldName, sectionState[index][fieldName]);
      if (error) errorArray.push([`${index}_${fieldName}`, error]);
      return errorArray;
    } else {
      return checkAllFieldsErrors(section, sectionState, index)
    }
  }

  // Pass fieldName if you are looking to set error for a single field (use case: editing a single field)
  // or skip fieldName if you are looking to set error for the entire entry(use case: adding/deleting an object)
  const setGroupErrors = (errorArray, section, index, fieldName) => {
    const newErrorMap = new Map(errors[section]);
    if (errorArray.length) {
      // Turn on the error indicator for the section
      setErrorIndicator(errorIndicator | (1 << getTabIndex(section)));
      // setting error in the errors object
      errorArray.forEach(error => newErrorMap.set(error[0], error[1]));
    } else {
      // Check if there was an error for this field
      if (fieldName) {
        //Check if there are errors elsewhere in the social media.
        // 1. Remove the field error if it exists.
        newErrorMap.delete(`${index}_${fieldName}`);
      } else {
        // Since there isn't error in the group, remove all the errors for the group
        Object.keys(SocialMediaFields).forEach(field => newErrorMap[section].delete(`${index}_${field}`));
      }
      // 2. If the error map is empty(other groups don't have errors), turn off the bit for the given section
      if (newErrorMap.size === 0) setErrorIndicator(errorIndicator & ~(1 << getTabIndex(section)));

    }
    // And finally set the updated error map.
    setErrors(prevError => ({
      ...prevError,
      [section]: newErrorMap
    }));
  }

  // Remove errors for a group when the group is deleted.
  const removeAndSetGroupErrors = (section, index) => {
    const newErrorMap = new Map(errors[section]);

    for (const key of newErrorMap.keys()) {
      if (String(key).startsWith(`${index}_`)) newErrorMap.delete(key);
    }

    setErrors(prevError => ({
      ...prevError,
      [section]: newErrorMap
    }));

    return newErrorMap;
  }

  const confirmGroupDelete = (sectionName, index) => {
    setAppDialogState({
      open: true,
      title: <Box>
        <Typography sx={{ margin: 0 }}>
          < DeleteIcon width={"2.5rem"} height={"2.5rem"} />
        </Typography>
        <Typography variant="h2" sx={{ fontSize: "1.5rem", fontFamily: "GilroySemiBold" }}>
          Delete Group?
        </Typography>
      </Box>,
      content: <Box sx={{ fontSize: "1rem", fontFamily: "GilroySemiBold", margin: "1rem 0" }}>
        Are you sure you want to delete {state[sectionName][index].name}?
      </Box>,
      firstBtnText: "Cancel",
      secondBtnText: "Delete",
      onClose: closeAppDialog,
      onConfirm: () => deleteGroup(sectionName, index),
    });
  };

  const setGroupsChanges = (section, field, index, value) => {
    // Case where we have some new entry in the social media or category group list and we are editing the new ones...
    const tabIndex = getTabIndex(section);
    if (index >= originalServerState.current[section].length) {
      // All codes before is "redundant" as we are already setting the changes when we are adding new entries, yet keeping it just for security
      setChangeIndicator(changeIndicator | (1 << tabIndex));
      setChangeObject(prev => ({
        ...prev,
        [section]: changeObject[section].set(`${index}_${field}`, value)
      }));
      return;
    }

    // NOTE: doesn't matter if the index doesn't match the originalServerState if the index is changed, that means the list is changed.
    const originalValue = originalServerState.current[section][index][field];
    if (value !== originalValue) {
      const newChangeIndicator = changeIndicator | (1 << tabIndex);
      setChangeIndicator(newChangeIndicator);
      setChangeObject(prev => ({
        ...prev,
        [section]: changeObject[section].set(`${index}_${field}`, value)
      }));
    } else {
      //If value was changed earlier for the field but reverted back:
      const newChangeMap = new Map(changeObject[section]);
      newChangeMap.delete(`${index}_${field}`);

      if (!newChangeMap.size) {
        const newChangeIndicator = changeIndicator & ~(1 << tabIndex);
        setChangeIndicator(newChangeIndicator);
      }
      setChangeObject(prev => ({
        ...prev,
        [section]: newChangeMap
      }));
    }
  }

  const closeAppDialog = () => setAppDialogState({ ...appDialogState, open: false });

  const deleteGroup = (section, index) => {
    const tabIndex = getTabIndex(section);
    const toBeDeletedGroup = state[section][index];
    // Turing on the change indicator when we are deleting a group saved on server. Not the new ones created.
    // Only Social Media Groups saved on server have _id property
    if (section === SectionNames.socialMediaTab) {
      if (toBeDeletedGroup._id) {
        // Turn on the change indicator for the section
        setChangeIndicator(changeIndicator | (1 << tabIndex));
        const newChangeMap = new Map(changeObject[section]);
        // Deleting all the fields related to the deleted group
        for (const key of newChangeMap.keys()) {
          if (String(key).startsWith(`${index}_`)) newChangeMap.delete(key);
        }
        // Adding the deleted field to the change object without any index but with deleted prefix so that it can be mutated later
        newChangeMap.set(`$deleted_${toBeDeletedGroup._id}`);
        setChangeObject(prevChange => ({
          ...prevChange,
          [section]: newChangeMap
        }));

        const errMap = removeAndSetGroupErrors(section, index);
        if (errMap.size === 0) {
          // Turn off the bit for the section in the error indicator.
          setErrorIndicator(errorIndicator & ~(1 << tabIndex));
        }
      } else {
        const newChangeMap = new Map(changeObject[section]);
        for (const key of newChangeMap.keys()) {
          if (String(key).startsWith(`${index}_`)) newChangeMap.delete(key);
        }

        setChangeObject(prev => ({ ...prev, [section]: newChangeMap }));

        // If no changes remain, turn off the change indicator bit for this section.
        if (!newChangeMap.size) {
          setChangeIndicator(changeIndicator & ~(1 << tabIndex));
        }

        const errMap = removeAndSetGroupErrors(section, index);
        if (errMap.size === 0) {
          setErrorIndicator(errorIndicator & ~(1 << tabIndex));
        }
      }
    } else {
      // Turn on the change indicator regardless of whether the group was saved on server or not. We don't have category group _id.
      setChangeIndicator(changeIndicator | (1 << tabIndex));
      const newChangeMap = new Map(changeObject[section]);

      // Remove all the changes related to this group from the change object.
      for (const key of newChangeMap.keys()) {
        if (String(key).startsWith(`${index}_`)) newChangeMap.delete(key);
      }

      // Adding the deleted field to the change object without any index but with deleted prefix so that it can be mutated later 
      newChangeMap.set(`$deleted_${crypto.randomUUID()}`);
      setChangeObject(prev => ({
        ...prev,
        [section]: newChangeMap
      }));

      // remove errors for the deleted group
      const errArr = removeAndSetGroupErrors(section, index);

      // If there are no errors left in the section, turn off the bit for the section in the error indicator.
      if (!errArr.size) {
        // Turn off the bit for 12th - Category Group in the error indicator.
        setErrorIndicator(errorIndicator & ~(1 << tabIndex));
      }
    }

    dispatch({ type: section, payload: state[section].filter((x, i) => index !== i) });
    closeAppDialog();
  }
  //#endregion

  const uploadLogoFile = (data, file, fieldName, sectionName) => {
    fileUploadApi(file, data, fieldName, sectionName);
  };

  const fileUploadApi = (file, data, fieldName, sectionName) => {
    setImageLoading(true);
    setImageType(fieldName);
    const formData = new FormData();
    formData.append("image", file);

    Api.uploadImageFile(formData).then((response) => {
      if (response.success) {
        setImageType(null);
        data["name"] = response.data.fileId;
        data["image"] = response.data.location;
        data["upload"] = true;
        if (sectionName === SectionNames.socialMediaTab || sectionName === SectionNames.categoryGroupsTab) {
          const newList = [...state[sectionName]];
          if (sectionName === SectionNames.socialMediaTab) {
            newList[fieldName].icon = data;
            // Reset the error for this field
            setGroupErrors([], sectionName, fieldName, SocialMediaFields.icon);
          } else {
            newList[fieldName].img = data;
            // Reset the error for this field
            setGroupErrors([], sectionName, fieldName, CategoriesFields.img);
          }
          dispatch({ type: sectionName, payload: newList });
        } else dispatch({ type: sectionName, payload: { fieldName: fieldName, value: data } });

        // Considering all image updates lead to a change in state
        const newChangeIndicator = changeIndicator | (1 << getTabIndex(sectionName));
        setChangeIndicator(newChangeIndicator);

        if (sectionName === SectionNames.socialMediaTab) {
          const iconKey = `${fieldName}_${SocialMediaFields.icon}`;
          setChangeObject(prev => ({
            ...prev,
            [sectionName]: changeObject[sectionName].set(iconKey, data)
          }));
        } else if (sectionName === SectionNames.categoryGroupsTab) {
          const imgKey = `${fieldName}_${CategoriesFields.img}`;
          setChangeObject(prev => ({
            ...prev,
            [sectionName]: changeObject[sectionName].set(imgKey, data)
          }));
        } else {
          setChangeObject(prev => ({
            ...prev,
            [sectionName]: changeObject[sectionName].set(fieldName, data)
          }));
        }


        setImageLoading(false);
      } else {
        setImageType(null);
        setImageLoading(false);
        setMsgAlert({ open: true, message: response.msg, msgType: "error" });
      }
    }).catch(() => {
      setImageType(null);
      setImageLoading(false);
      setMsgAlert({ open: true, message: "Image upload failed. Please try again.", msgType: "error" });
    });
  };

  const colorValidation = (val) => {
    return /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(val);
  };

  const handleStateChange = (sectionName, fieldName, value) => {
    checkForInputErrors(sectionName, fieldName, value);
    checkForStateChanges(sectionName, fieldName, value);
    dispatch({ type: sectionName, payload: { fieldName, value } })
  }

  const checkForInputErrors = (sectionName, fieldName, value) => {
    //Currently checking for color inputs only
    switch (sectionName) {
      case SectionNames.ImagesTab:
        return;
      case SectionNames.ARPageTab:
        if (
          MetaData[sectionName].normalRender[fieldName]?.type !==
          FieldTypes.color
        )
          return;
        break;
      case SectionNames.MenuListingTab:
      case SectionNames.MenuDetailsPopupTab:
      case SectionNames.CartTab:
      case SectionNames.CheckoutTab:
      case SectionNames.OrderSuccessTab:
      case SectionNames.OrderHistoryTab:
      case SectionNames.OrderDetailsTab:
      case SectionNames.AddAddressTab:
      case SectionNames.AddressListTab:
      case SectionNames.PaymentSettingsTab:
        if (MetaData[sectionName][fieldName]?.type !== FieldTypes.color) return;
        break;
      case SectionNames.MultiLanguageTab:
        return;
      case SectionNames.socialMediaTab:
        return;
      case SectionNames.categoryGroupsTab:
        return;
      default:
        return;
    }
    // Empty value is allowed
    const error = !value || colorValidation(value) ? null : COLOR_ERROR_TEXT;

    if (error) {
      setErrorIndicator(errorIndicator | (1 << currentTab));
      // setting error in the errors object
      setErrors(prevError => ({
        ...prevError,
        [sectionName]: prevError[sectionName].set(fieldName, error)
      }));
    } else {
      //Check if there are errors elsewhere in the category group.
      // 1. If error has this particular field, then remove it.
      const newSectionErrorMap = new Map(errors[sectionName]);
      newSectionErrorMap.delete(fieldName);

      // 2. If still have errors, update the error map
      if (!newSectionErrorMap.size) {
        // Turn off the bit for 12th - Category Group in the error indicator.
        setErrorIndicator(errorIndicator & ~(1 << currentTab));
      }
      // And finally set the updated error map.
      setErrors(prevError => ({
        ...prevError,
        [sectionName]: newSectionErrorMap
      }));
    }
  }

  const checkForStateChanges = (sectionName, fieldName, value) => {
    const originalValue = originalServerState.current[sectionName][fieldName];

    const isValueChanged = !value && !originalValue ? false : value != originalValue;
    // If value has change from original
    if (isValueChanged) {
      // Flagging the change Indicator
      const newChangeIndicator = changeIndicator | (1 << currentTab);
      setChangeIndicator(newChangeIndicator);
      // Updating the changeObject
      setChangeObject(prev => {
        return ({
          ...prev,
          [sectionName]: prev[sectionName].set(fieldName, value)
        })
      });
    } else {
      // If value has not changed, remove it from the changeObject
      // 1. If changes were previously set for this field, remove it.
      const newChangeObject = new Map(changeObject[sectionName]);
      newChangeObject.delete(fieldName);
      setChangeObject(prev => {
        return ({
          ...prev,
          [sectionName]: newChangeObject
        })
      });

      // If the change object has doesn't have any changes, turn off the bit for the section in the change indicator.
      if (!newChangeObject.size) {
        const newChangeIndicator = changeIndicator & ~(1 << currentTab);
        setChangeIndicator(newChangeIndicator);
      }

      // finally update the change object
      setChangeObject(prev => ({
        ...prev,
        [sectionName]: newChangeObject
      }));
    }
  }

  const handleFileRemove = (sectionName, fieldName) => {
    handleStateChange(sectionName, fieldName, getImgDetail("", fieldName));
  }

  const getTabPanelContent = tabNumber => {
    if (tabNumber === 0)
      return (
        <ImagesTab
          data={state[SectionNames.ImagesTab]}
          sectionName={PageTabs[tabNumber].stateKey}
          imageLoading={imageLoading}
          imageType={imageType}
          uploadLogoFile={uploadLogoFile}
          handleChange={handleStateChange}
          onRemoveFile={handleFileRemove}
        />
      );
    else if (tabNumber === 1)
      return (
        <ARTabPanel
          SectionMetaData={PageTabs[tabNumber].metaData}
          sectionName={PageTabs[tabNumber].stateKey}
          data={state[PageTabs[tabNumber].stateKey]}
          styles={{}}
          errors={errors[PageTabs[tabNumber].stateKey]}
          handleChange={handleStateChange}
          fileUploadApi={fileUploadApi}
        />
      );
    else if (tabNumber === 4)
      return (
        <TableOrderingMenuLayoutTab
          data={state[SectionNames.TableOrderingMenuLayoutTab]}
          handleChange={handleStateChange}
          sectionName={SectionNames.TableOrderingMenuLayoutTab}
          restaurantData={props.restaurantData}
        />
      );
    else if (tabNumber < 12)
      return (
        <TabPanelTemplate
          SectionMetaData={PageTabs[tabNumber].metaData}
          sectionName={PageTabs[tabNumber].stateKey}
          data={state[PageTabs[tabNumber].stateKey]}
          styles={{}}
          errors={errors[PageTabs[tabNumber].stateKey]}
          handleChange={handleStateChange}
          fileUploadApi={fileUploadApi}
        />
      );
    else if (tabNumber === 12)
      return (
        <SocialMediaPage
          styles={styles}
          socialMediaList={state[SectionNames.socialMediaTab]}
          imageLoading={imageLoading}
          imageType={imageType}
          uploadLogoFile={uploadLogoFile}
          handleChange={handleGroupChange}
          onAddEntry={addGroupEntry}
          onDeleteEntry={confirmGroupDelete}
          errors={errors[SectionNames.socialMediaTab]}
        />
      );
    else if (tabNumber === 13)
      return (
        <CategoryGroupPage
          categoryGroupList={state[SectionNames.categoryGroupsTab]}
          styles={styles}
          imageLoading={imageLoading}
          imageType={imageType}
          uploadLogoFile={uploadLogoFile}
          handleChange={handleGroupChange}
          onRemoveCategory={onRemoveCategory}
          onCategoriesListEdit={onCategoriesListEdit}
          onAddEntry={addGroupEntry}
          onDeleteEntry={confirmGroupDelete}
          errors={errors}
        />
      );
    else if (tabNumber === 14)
      return (
        <PaymentSettingsTab
          data={state[SectionNames.PaymentSettingsTab]}
          handleChange={handleStateChange}
          styles={styles}
          errors={errors[PageTabs[tabNumber].stateKey]}
        />
      );
    else if (tabNumber === 15)
      return (
        <MultiLanguageTab
          data={state[SectionNames.MultiLanguageTab]}
          handleChange={handleStateChange}
          sectionName={SectionNames.MultiLanguageTab}
          errors={errors[SectionNames.MultiLanguageTab]}
        />
      );
    else if (tabNumber === 16)
      return (
        <WhiteListDomain restaurantData={props.restaurantData} />
      );
  }

  const onSave = () => {
    if (errorIndicator > 0) return;

    setSaving(true);
    const newRestaurantData = mapStateToServer(props.restaurantData, state);

    Api.updateRestaurant(newRestaurantData, restaurantId).then((response) => {
      if (response.success) {
        setMsgAlert({ open: true, message: response.msg, msgType: "success" });
        //update the original state and change indicator
        originalServerState.current = structuredClone(state);
        setChangeIndicator(0);
        setChangeObject(getDefaultChangeObject());
      } else {
        setMsgAlert({ open: true, message: response.msg, msgType: "error" });
      }
    }).finally(() => setSaving(false));
  }

  const onReset = () => {
    dispatch({ type: "STATE", payload: structuredClone(originalServerState.current) });
    setChangeIndicator(0);
    setChangeObject(getDefaultChangeObject());
    setErrorIndicator(0);
    setErrors(getDefaultErrorObject());
  }

  const btnDisabled = imageLoading || errorIndicator > 0 || changeIndicator === 0 || saving;
  const resetBtnDisabled = changeIndicator === 0;

  return (
    <Container
      page={"restaurants"}
      searchVal={""}
      onSearch={() => null}
      searchLabel=""
    >
      {loading ? (
        <Loader height={{ height: window.innerHeight - 150 }} />
      ) : (
        <>
          <Grid container item xs={12}>
            <Grid container xs={12}>
              <Grid item xs={12} mb={2}>
                <RestaurantHeader
                  title={"Portal Settings"}
                  onBackBtnClick={() => navigate("/restaurant/" + restaurantId)}
                />
              </Grid>

              <Grid item xs={12} className={styles.mainView} px={3} pt={2}
                sx={{
                  width: `calc(100vw - ${props.drawerOpen ? '23.5rem' : '8rem'})`,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Grid item xs={12} flexGrow={1}>
                  <Tabs
                    value={currentTab}
                    onChange={(e, val) => setCurrentTab(val)}
                    variant="scrollable"
                    scrollButtons={true}
                    sx={{
                      width: '100%',
                      borderBottom: "1px solid #AAA",
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#000",
                        borderBottom: "3px solid #000",
                      },
                    }}
                  >
                    {PageTabs.map((tab, index) => (
                      <Tab key={index} label={tab.label}
                        sx={{
                          textTransform: "none",
                          minWidth: 150,
                          fontFamily: "GilroyBold",
                          "&.Mui-selected": {
                            color: "#000",
                          },
                        }}
                      />
                    ))}
                  </Tabs>
                  <TabPanel sx={{
                    '& .MuiBox-root': {
                      backgroundColor: 'brown',
                    }
                  }}>
                    <Box
                      sx={{
                        padding: '1rem',
                        background: '#f9f9f9',
                        margin: '0.5rem 0',
                        borderRadius: '0.5rem'
                      }}
                    >
                      {getTabPanelContent(currentTab)}
                    </Box>
                  </TabPanel>
                </Grid>

                <Grid container item direction="row" xs={12} md={12} mb={2} gap={2} justifyContent={"flex-end"} alignItems={"flex-end"}>
                  <Button
                    type="button"
                    disabled={resetBtnDisabled}
                    className={styles.saveBtn}
                    sx={{
                      opacity: resetBtnDisabled ? 0.2 : 1,
                      backgroundColor: "#fff !important",
                      color: "#000 !important",
                      border: "2px solid #000 !important",
                    }}
                    onClick={resetBtnDisabled ? null : onReset}
                  >
                    Reset
                  </Button>
                  <Button
                    type="button"
                    disabled={btnDisabled}
                    className={styles.saveBtn}
                    sx={{ opacity: btnDisabled ? 0.2 : 1 }}
                    onClick={btnDisabled ? null : onSave}
                  >
                    {saving ? "Please Wait" : "Save"}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() =>
          setMsgAlert({ ...msgAlert, open: false })
        }
      />

      <CategoryGroupDialog
        open={categoryDialogState.open}
        onClose={closeCategoryDialog}
        data={{
          categories: state[SectionNames.categoryGroupsTab][categoryDialogState.index]?.categories || [],
          restaurantCategories,
          categoriesInGroupsList: getCategoriesInGroupsList,
        }}
        handleToggleCategory={handleToggleCategory}
        handleClose={closeCategoryDialog}
      />

      <AppDialog
        open={appDialogState.open}
        onClose={appDialogState.onClose}
        content={appDialogState.content}
        title={appDialogState.title}
        firstBtnContent={appDialogState.firstBtnText}
        secondBtnContent={appDialogState.secondBtnText}
        firstBtnClickHandler={appDialogState.onClose}
        secondBtnClickHandler={appDialogState.onConfirm}
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  drawerOpen: state.userData.drawerOpen,
  restaurantData: state.userData.restaurantData
});

const mapDispatchToProps = (dispatch) => {
  return {
    updateRestaurantData: (data) => dispatch(updateRestaurantData(data)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomRestaurantInfo);
